import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import serviceRequestService from '../../api/serviceRequestService';
import searchService from '../../api/searchService';
import { useNotifications } from '../../context/NotificationContext';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { dateTime } from '../../utils/formatters';

const TABS = ['NEW', 'REVIEWED', 'FORWARDED', 'CLOSED', ''];

export default function ServiceRequestQueue() {
  const { t } = useTranslation();
  const notify = useNotifications();
  const [tab, setTab] = useState('NEW');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [technicians, setTechnicians] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    serviceRequestService.list({ page, size: 10, ...(tab ? { status: tab } : {}) })
      .then(setData)
      .catch((e) => notify.apiError(e))
      .finally(() => setLoading(false));
  }, [page, tab, notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    searchService.listTechnicians({ size: 100 }).then((p) => setTechnicians(p.content || [])).catch(() => {});
  }, []);

  const patch = async (r, payload, msg) => {
    setBusy(r.id);
    try {
      await serviceRequestService.update(r.id, payload);
      notify.success(msg);
      load();
    } catch (e) { notify.apiError(e); } finally { setBusy(null); }
  };

  const forward = async (r) => {
    setBusy(r.id);
    try {
      await serviceRequestService.forward(r.id);
      notify.success(t('serviceRequest.forward'));
      load();
    } catch (e) { notify.apiError(e); } finally { setBusy(null); }
  };

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1.25rem' }}>
        <h1>{t('serviceRequest.adminTitle')}</h1>
        <div className="toolbar">
          {TABS.map((s) => (
            <button key={s || 'all'} className={`chip ${tab === s ? 'active' : ''}`}
              onClick={() => { setTab(s); setPage(0); }}>
              {s ? t(`serviceRequest.reqStatus.${s}`) : t('common.all')}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : data.content.length === 0 ? (
          <EmptyState icon="clipboard-check" title={t('admin.queueClear')} />
        ) : (
          <div className="stack">
            {data.content.map((r) => (
              <div key={r.id} className="card pad stack" style={{ gap: '.5rem' }}>
                <div className="spread">
                  <div>
                    <strong>{r.clientName}</strong>{' '}
                    <span className="muted" style={{ fontSize: '.88rem' }}>· {r.clientPhone}{r.clientEmail ? ` · ${r.clientEmail}` : ''}</span>
                  </div>
                  <span className="badge gray">{t(`serviceRequest.reqStatus.${r.status}`)}</span>
                </div>
                <p>{r.description}</p>
                <div className="row" style={{ gap: '1.5rem', fontSize: '.88rem' }}>
                  <span className="muted"><i className="bi bi-geo-alt" /> {r.city}</span>
                  {r.categoryName && <span className="muted">{r.categoryName}</span>}
                  <span className="muted">{dateTime(r.createdAt)}</span>
                </div>

                <div className="row" style={{ gap: '.5rem', alignItems: 'center' }}>
                  <select
                    value={r.technicianId || ''}
                    disabled={busy === r.id}
                    onChange={(e) => patch(r, { technicianId: e.target.value }, t('serviceRequest.assignTechnician'))}
                  >
                    <option value="">{t('serviceRequest.assignTechnician')}</option>
                    {technicians.map((x) => (
                      <option key={x.id} value={x.id}>{x.firstName} {x.lastName} — {x.technicianTypeName}</option>
                    ))}
                  </select>
                  {r.status === 'NEW' && (
                    <button className="btn ghost sm" disabled={busy === r.id}
                      onClick={() => patch(r, { status: 'REVIEWED' }, t('serviceRequest.markReviewed'))}>
                      {t('serviceRequest.markReviewed')}
                    </button>
                  )}
                  {r.technicianId && r.status !== 'FORWARDED' && r.status !== 'CLOSED' && (
                    <button className="btn accent sm" disabled={busy === r.id} onClick={() => forward(r)}>
                      {t('serviceRequest.forward')}
                    </button>
                  )}
                  {r.status !== 'CLOSED' && (
                    <button className="btn ghost sm" disabled={busy === r.id}
                      onClick={() => patch(r, { status: 'CLOSED' }, t('serviceRequest.close'))}>
                      {t('serviceRequest.close')}
                    </button>
                  )}
                </div>
                {r.handledBy && <span className="muted" style={{ fontSize: '.8rem' }}>{t('admin.reviewedBy', { who: r.handledBy })}</span>}
              </div>
            ))}
          </div>
        )}
        <Pagination page={page} totalPages={data.totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
